import React, { useContext, useEffect } from "react";
import { Field, useFormState } from "react-final-form";
import { useIntl } from "react-intl";
import { FieldArray } from "react-final-form-arrays";
import * as Validations from "../../../common/form/validator";
import { DropdownInput, TextInput } from "../../../common/fields";
import { Button } from "primereact/button";
import { FormContext } from "../../../common/context";
import { connect } from "react-redux";
import {
  defaultUserType,
  isEmailOptional,
  isSsoSetUp,
  USER_TYPE_OPTIONS,
} from "../../../../helpers/SsoHelper";

const emailRequired = Validations.composeValidators(
  Validations.required,
  Validations.checkEmailFormat
);

const AddUserForm = ({ roles, defaultRole, enterprise }) => {
  const intl = useIntl();
  const formProvider = useContext(FormContext);
  const formState = useFormState();
  const ssoSetUp = isSsoSetUp(enterprise);
  const newUser = ssoSetUp
    ? { roleId: defaultRole, profileType: defaultUserType(enterprise) }
    : { roleId: defaultRole };
  // Room for the user type column: the fields share the row.
  const fieldColumn = "col-12 md:col-6 lg:col";
  const emailOptional = (index) =>
    isEmailOptional(enterprise, formState.values?.users?.[index]?.profileType);

  useEffect(() => {
    formState.values?.users.forEach((element, index) => {
      const isVisited =
        formState?.visited?.[`users[${index}].username`] || false;
      if (!isVisited && element.email && element.username !== element.email) {
        formProvider.change(`users[${index}].username`, element.email);
      }
    });
  }, [formState.values?.users, formState?.visited, formProvider]);

  return (
    <FieldArray name="users">
      {({ fields }) =>
        fields.map((name, index) => (
          <div className="grid" key={index}>
            <div className={fieldColumn}>
              <Field
                name={`${name}.email`}
                component={TextInput}
                type="email"
                icon="pi-envelope"
                placeholder={intl.formatMessage({
                  id: "email",
                })}
                label={intl.formatMessage({
                  id: "email",
                })}
                required={!emailOptional(index)}
                validate={(value, allValues) =>
                  isEmailOptional(enterprise, allValues?.users?.[index]?.profileType)
                    ? Validations.checkEmailFormat(value)
                    : emailRequired(value, allValues)
                }
              />
            </div>
            <div className={fieldColumn}>
              <Field
                name={`${name}.username`}
                component={TextInput}
                type="text"
                icon="pi-user"
                placeholder={intl.formatMessage({
                  id: "username",
                })}
                label={intl.formatMessage({
                  id: "username",
                })}
                required
                validate={Validations.composeValidators(Validations.required)}
              />
            </div>
            <div className={fieldColumn}>
              <Field
                name={`${name}.firstName`}
                component={TextInput}
                type="text"
                label={intl.formatMessage({
                  id: "firstName",
                })}
                placeholder={intl.formatMessage({
                  id: "firstName",
                })}
                required
                validate={Validations.composeValidators(Validations.required)}
              />
            </div>
            <div className={fieldColumn}>
              <Field
                name={`${name}.lastName`}
                component={TextInput}
                type="text"
                label={intl.formatMessage({
                  id: "lastName",
                })}
                placeholder={intl.formatMessage({
                  id: "lastName",
                })}
                required
                validate={Validations.composeValidators(Validations.required)}
              />
            </div>
            <div className={fieldColumn}>
              <Field
                name={`${name}.roleId`}
                component={DropdownInput}
                options={roles}
                label={intl.formatMessage({
                  id: "role",
                })}
                placeholder={intl.formatMessage({
                  id: "role",
                })}
                required
                validate={Validations.composeValidators(Validations.required)}
              />
            </div>
            {(ssoSetUp && (
              <div className={fieldColumn}>
                <Field
                  name={`${name}.profileType`}
                  component={DropdownInput}
                  options={USER_TYPE_OPTIONS}
                  label={intl.formatMessage({ id: "user_type" })}
                  placeholder={intl.formatMessage({ id: "user_type" })}
                  required
                  validate={Validations.composeValidators(Validations.required)}
                />
              </div>
            )) ||
              null}
            <div className="col-12 md:col-12 lg:col-1">
              {(index === 0 && (
                <Button
                  type="button"
                  icon="pi pi-plus"
                  style={{ marginTop: "21px" }}
                  onClick={() =>
                    formProvider.push("users", newUser)
                  }
                />
              )) || (
                <Button
                  type="button"
                  icon="pi pi-trash"
                  style={{ marginTop: "21px" }}
                  onClick={() => fields.remove(index)}
                />
              )}
            </div>
          </div>
        ))
      }
    </FieldArray>
  );
};

const mapStateToProps = (state) => ({
  roles: state.roles?.data?.list || [],
  enterprise: state.global?.enterprise,
});

export default connect(mapStateToProps)(AddUserForm);
